import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ptBR, es } from "date-fns/locale";
import {
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Typography,
    makeStyles,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from "@material-ui/core";
import api from "../../services/api";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(2),
    },
    card: {
        width: "100%",
        maxWidth: 600,
    },
    title: {
        marginBottom: theme.spacing(2),
        textAlign: "center",
        color: theme.palette.primary.main,
        fontWeight: "bold",
    },
    section: {
        marginTop: theme.spacing(2),
        textAlign: "center" // Center content
    },
    label: {
        fontWeight: "bold",
        color: theme.palette.text.secondary,
    },
    value: {
        fontSize: "1.2rem",
        marginTop: theme.spacing(0.5),
    },
    actions: {
        marginTop: theme.spacing(4),
        display: "flex",
        justifyContent: "center",
        gap: theme.spacing(2),
    },
    logo: {
        width: "100%",
        maxWidth: "200px",
        margin: "0 auto 20px auto",
        display: "block"
    }
}));

const PublicAppointment = () => {
    const classes = useStyles();
    const { uuid } = useParams();
    const [appointment, setAppointment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
    const [newDate, setNewDate] = useState("");
    const [cancelModalOpen, setCancelModalOpen] = useState(false);

    useEffect(() => {
        const fetchAppointment = async () => {
            try {
                const { data } = await api.get(`/public/appointments/${uuid}`);
                setAppointment(data);
                setLoading(false);
            } catch (err) {
                toast.error("Erro ao carregar agendamento");
                setLoading(false);
            }
        };
        fetchAppointment();
    }, [uuid]);

    const handleReschedule = async () => {
        try {
            await api.put(`/public/appointments/${uuid}/reschedule`, { date: newDate });
            toast.success("Agendamento reagendado com sucesso!");
            setRescheduleModalOpen(false);

            // Refresh
            const { data } = await api.get(`/public/appointments/${uuid}`);
            setAppointment(data);
        } catch (err) {
            toast.error("Erro ao reagendar. Verifique a disponibilidade.");
        }
    };

    const handleCancel = async () => {
        try {
            await api.delete(`/public/appointments/${uuid}/cancel`);
            toast.success("Agendamento cancelado com sucesso!");
            setCancelModalOpen(false);

            // Refresh
            const { data } = await api.get(`/public/appointments/${uuid}`);
            setAppointment(data);
        } catch (err) {
            toast.error("Erro ao cancelar agendamento.");
        }
    };

    if (loading) {
        return (
            <div className={classes.root}>
                <CircularProgress />
            </div>
        );
    }

    if (!appointment) {
        return (
            <div className={classes.root}>
                <Typography variant="h5">Agendamento não encontrado.</Typography>
            </div>
        );
    }

    const locale = i18n.language === "pt" ? ptBR : es;

    return (
        <div className={classes.root}>
            <Container maxWidth="sm">
                <Card className={classes.card} elevation={3}>
                    <CardContent>
                        {/* Placeholder for Logo - You can add your company logo here */}
                        {/* <img src="/logo.png" alt="Logo" className={classes.logo} /> */}

                        <Typography variant="h4" className={classes.title}>
                            Detalhes do Agendamento
                        </Typography>

                        <Grid container spacing={3} className={classes.section}>
                            <Grid item xs={12}>
                                <Typography className={classes.label}>Status:</Typography>
                                <Typography
                                    className={classes.value}
                                    style={{
                                        color: appointment.status === 'scheduled' ? '#4caf50' :
                                            appointment.status === 'cancelled' ? '#f44336' : '#9e9e9e',
                                        fontWeight: "bold"
                                    }}
                                >
                                    {appointment.status === 'scheduled' ? 'Confirmado' :
                                        appointment.status === 'cancelled' ? 'Cancelado' : appointment.status}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography className={classes.label}>Serviço:</Typography>
                                <Typography className={classes.value}>{appointment.service?.name}</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography className={classes.label}>Profissional:</Typography>
                                <Typography className={classes.value}>{appointment.professional?.name}</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography className={classes.label}>Data e Hora:</Typography>
                                <Typography className={classes.value}>
                                    {format(parseISO(appointment.date), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale })}
                                </Typography>
                            </Grid>

                            {appointment.observation && (
                                <Grid item xs={12}>
                                    <Typography className={classes.label}>Observações:</Typography>
                                    <Typography className={classes.value}>{appointment.observation}</Typography>
                                </Grid>
                            )}
                        </Grid>

                        {/* Actions only if scheduled */}
                        {appointment.status === 'scheduled' && (
                            <div className={classes.actions}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setRescheduleModalOpen(true)}
                                >
                                    Reagendar
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => setCancelModalOpen(true)}
                                >
                                    Cancelar
                                </Button>
                            </div>
                        )}

                        {appointment.status === 'cancelled' && (
                            <div className={classes.section}>
                                <Typography variant="body1" color="error">
                                    Este agendamento foi cancelado.
                                </Typography>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </Container>


            {/* Modal Reagendar */}
            <Dialog open={rescheduleModalOpen} onClose={() => setRescheduleModalOpen(false)}>
                <DialogTitle>Reagendar Atendimento</DialogTitle>
                <DialogContent>
                    <Typography style={{ marginBottom: 20 }}>
                        Escolha uma nova data e horária. (Implementar DatePicker aqui)
                    </Typography>
                    <TextField
                        label="Nova Data (YYYY-MM-DD HH:mm)"
                        type="datetime-local"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        fullWidth
                        value={newDate}
                        onChange={(e) => setNewDate(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRescheduleModalOpen(false)} color="primary">
                        Cancelar
                    </Button>
                    <Button onClick={handleReschedule} color="primary" variant="contained">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal Cancelar */}
            <Dialog open={cancelModalOpen} onClose={() => setCancelModalOpen(false)}>
                <DialogTitle>Confirmar Cancelamento</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja cancelar este agendamento? Esta ação não pode ser desfeita.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelModalOpen(false)} color="primary">
                        Não, manter
                    </Button>
                    <Button onClick={handleCancel} color="secondary" variant="contained">
                        Sim, cancelar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PublicAppointment;
